import { Component, OnInit, Input } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  FormArray, FormControl
} from '@angular/forms';

// PrimeNG
import { TabViewModule } from 'primeng/tabview';
import { ButtonModule } from 'primeng/button';

// Shared components
import { MapboxHeaderComponent } from '../../shared/mapbox-header/mapbox-header.component';
import { DisplayImagesComponent } from '../../shared/display-images/display-images.component';
import { InputDisplayGridComponent } from '../../shared/input-display-grid/input-display-grid.component';
import { UserMenuComponent } from '../../shared/user-menu/user-menu.component';

// Model & service
import { HeroReportService } from '../../model/hero-report/hero-report.service';
import { HeroReport } from '../../model/hero-report/hero-report.model';
import { HeroReportImageService } from '../../model/hero-report-image/hero-report-image.service';

import { PermissionService } from '../../permissions.service';

import { CLASSIFICATION_OPTIONS } from '../../../../ordnances';

import { ItemFormComponent } from './item-form/item-form.component';
import { EventFormComponent } from './event-form/event-form.component';
import { EvacuationFormComponent } from './evacuation-form/evacuation-form.component';
import { SreReportViewComponent } from '../sre-report/sre-report-view/sre-report-view.component';

import { Image } from '../../model/hero-report-image/image.model';
import { mgrsValidator } from '../../utilities/location';
import { mgrsFromLatLon, normalizeMgrs } from '../../utilities/mgrs';
import { MAX_MGRS_LENGTH } from '../../utilities/sreReport';
import { HeroReportImageStorageService } from '../../model/hero-report-image/hero-report-image-storage.service';

type ErrorMessageKey = 'required' | 'number' | 'dateTime' | 'email';

/** Convert boolean or string to "Yes"/"No"/"Unknown" for read-only displays. */
function convertToYesNoOrUnknown(value: string | boolean | null): string {
  if (value === 'Yes' || value === true) {
    return $localize`:@@common.yes:Yes`;
  } else if (value === 'No' || value === false) {
    return $localize`:@@common.no:No`;
  } else {
    return $localize`:@@common.unknown:Unknown`;
  }
}

function atLeastOneOverallImageValidator(control: AbstractControl): ValidationErrors | null {
  const overallImages = control.get('overallImages');
  if (overallImages && Array.isArray(overallImages.value) && overallImages.value.length > 0) {
    return null;
  }
  return { noOverallImage: true };
}

@Component({
  selector: 'app-hero-report',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TabViewModule,
    ButtonModule,
    MapboxHeaderComponent,
    DisplayImagesComponent,
    InputDisplayGridComponent,
    UserMenuComponent,
    ItemFormComponent,
    EventFormComponent,
    EvacuationFormComponent,
    SreReportViewComponent
  ],
  templateUrl: './hero-report.component.html',
  styleUrls: ['./hero-report.component.scss'],
})
export class HeroReportComponent implements OnInit {
  heroReportId: string;
  heroReport: HeroReport;

  classificationOptions = CLASSIFICATION_OPTIONS.map((option) => ({
    label: option.label + ' ' + option.size,
    value: option.key,
  }));

  classificationLabel = 'Unknown';

  /** SRE reports have their own tabs (item and photos, details) instead of the UXO event and evacuation tabs. */
  get isSre(): boolean {
    return this.heroReport?.reportType === 'SRE';
  }

  // p-tabView controls
  activeTabIndex = 0;
  currentlyEditingTab: number | null = null;

  // Three forms
  itemForm!: FormGroup;
  eventForm!: FormGroup;
  evacuationForm!: FormGroup;

  // read-only arrays for grids
  itemRows: Array<{ label?: string; value?: string }> = [];
  standardOptions: Array<{ label?: string; value?: string }> = [];
  evacuationOptions1: Array<{ label?: string; value?: string }> = [];
  evacuationOptions2: Array<{ label?: string; value?: string }> = [];
  evacuationOptions3: Array<{ label?: string; value?: string }> = [];
  images: Array<{ label?: string; value?: string[] }> = [];

  canViewEOD = false;
  canEditHero = false;
  canDeleteHero = false;

  // Reusable radio button options
  yesNoUnknownOptions = [
    { label: $localize`:@@common.yes:Yes`, value: true },
    { label: $localize`:@@common.no:No`, value: false },
    { label: $localize`:@@common.unknown:Unknown`, value: null },
  ];

  yesNoOptions = [
    { label: $localize`:@@common.yes:Yes`, value: true },
    { label: $localize`:@@common.no:No`, value: false },
  ];

  // For the <app-input-plus-picker> (First Noticed Date)
  dateOptions = [
    { label: $localize`:@@date.days:Days`, value: 'days' },
    { label: $localize`:@@date.weeks:Weeks`, value: 'weeks' },
    { label: $localize`:@@date.months:Months`, value: 'months' },
    { label: $localize`:@@date.years:Years`, value: 'years' },
  ];

  // Object URLs for the images already downloaded from S3, by image id.
  private imageUrls = new Map<string, string>();

  overallImages: Image[] = [];
  noseImages: Image[] = [];
  tailImages: Image[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private heroReportService: HeroReportService,
    private permissionService: PermissionService,
    private imageStorage: HeroReportImageStorageService
  ) { }

  ngOnInit(): void {
    this.buildForms();

    this.heroReportId = this.route.snapshot.paramMap.get('id')!;
    this.route.data.subscribe(data => {
      this.heroReport = data['heroReport']
      this.populateForms();
      this.setReadOnlyData();

      this.overallImages = this.findImagesByType('OVERALL');
      this.noseImages = this.findImagesByType('NOSE');
      this.tailImages = this.findImagesByType('TAIL');
      this.loadImageUrls();

      // Example role check for EOD
      this.canViewEOD = this.permissionService.can('view:eod');
      this.canEditHero = this.permissionService.can('edit:hero');
      this.canDeleteHero = this.permissionService.can('delete:hero');
    })
  }

  findImagesByType(type: string): Image[] {
    return (
      this.heroReport.heroReportImages?.filter((img) => img.type === type) || []
    ).map((img) => ({
      tag: img.type,
      url: this.imageUrls.get(img.id) ?? img.url,
    }) as Image);
  }

  /** Downloads the report's images from S3, then refreshes everything that shows them. */
  private async loadImageUrls(): Promise<void> {
    // The SRE view loads its own photos.
    if (this.isSre) return;
    const heroReportImages = this.heroReport.heroReportImages || [];
    await Promise.all(
      heroReportImages
        .filter((img) => img.id && img.s3ObjectKey)
        .map(async (img) => {
          const url = await this.imageStorage.getObjectUrl(img);
          if (url) {
            this.imageUrls.set(img.id, url);
          }
        })
    );

    this.overallImages = this.findImagesByType('OVERALL');
    this.noseImages = this.findImagesByType('NOSE');
    this.tailImages = this.findImagesByType('TAIL');
    this.buildImageGroups();
  }

  /** Build 3 separate forms. */
  buildForms(): void {
    // ITEM TAB
    this.itemForm = this.fb.group({
      possibleIdentification: [''],
      evacuationPerimeterM: [null],
      latitude: [null],
      longitude: [null],
      mgrs: ['', [Validators.required, Validators.maxLength(MAX_MGRS_LENGTH), mgrsValidator]],
      overallImages: this.fb.array([]),
      noseImages: this.fb.array([]),
      tailImages: this.fb.array([]),
    });

    // EVENT TAB
    this.eventForm = this.fb.group({
      firstNoticedDate: null,
      pocName: [''],
      pocPhone: [''],
      previousFindings: [null], // yes/no
      wasItemMoved: [null], // yes/no
      liquidOrCrystallization: [null], // yes/no
      deadAnimalsOrFoliage: [null], // yes/no
      casualties: [''],
    });

    // EVACUATION TAB
    this.evacuationForm = this.fb.group({
      evacuationPerimeterM: ['', this.numberValidator()],
      infrastructurePresent: [null],
      radioTransmitters: [null],
      powerLines: [null],
      fuelTanks: [null],
      buildingsInFragZone: [null],
      additionalHazards: [''],
    });
  }

  private numberValidator() {
    return Validators.pattern(/^-?\d+(\.\d+)?$/);
  }

  populateForms(): void {
    if (!this.heroReport) return;

    this.itemForm.patchValue({
      possibleIdentification: this.heroReport.possibleIdentification || '',
    });

    // Without events, so that the MGRS does not move the position the report has (see linkLocationControls).
    this.itemForm.patchValue(
      {
        latitude: this.heroReport.latitude,
        longitude: this.heroReport.longitude,
        // Reports saved before UXO reports had an MGRS show the one their position is in.
        mgrs:
          this.heroReport.mgrs ||
          mgrsFromLatLon(this.heroReport.latitude, this.heroReport.longitude) ||
          '',
      },
      { emitEvent: false },
    );

    this.eventForm.patchValue({
      firstNoticedDate: this.heroReport.firstNoticedDate,
      pocName: this.heroReport.pocName || '',
      pocPhone: this.heroReport.pocPhone || '',
      previousFindings: this.heroReport.previousFindings,
      wasItemMoved: this.heroReport.wasItemMoved,
      liquidOrCrystallization: this.heroReport.liquidOrCrystallization,
      deadAnimalsOrFoliage: this.heroReport.deadAnimalsOrFoliage,
      casualties: this.heroReport.casualties || '',
    });

    const mergedAdditional = [
      this.heroReport.additionalHazards || '',
      this.heroReport.accessibility || '',
    ]
      .filter(Boolean)
      .join('\n');

    this.evacuationForm.patchValue({
      evacuationPerimeterM: this.heroReport.evacuationPerimeterM || '',
      infrastructurePresent: this.heroReport.infrastructurePresent,
      radioTransmitters: this.heroReport.radioTransmitters,
      powerLines: this.heroReport.powerLines,
      fuelTanks: this.heroReport.fuelTanks,
      buildingsInFragZone: this.heroReport.buildingsInFragZone,
      additionalHazards: mergedAdditional,
    });
  }

  setReadOnlyData(): void {
    if (!this.heroReport) return;

    const classificationLabels = this.heroReport.possibleIdentification
      ? this.heroReport.possibleIdentification
        .map((id) => {
          const matched = CLASSIFICATION_OPTIONS.find(
            (opt) => opt.key === id.trim(),
          );
          return matched ? matched.label + ' ' + matched.size : null;
        })
        .filter((label) => label !== null)
      : [];

    this.classificationLabel =
      classificationLabels.length > 0
        ? classificationLabels.join(', ')
        : $localize`:@@common.unknown:Unknown`;

    this.itemRows = [
      {
        label: $localize`:@@heroReport.classification:Classification`,
        value: this.classificationLabel,
      },
      {
        label: $localize`:@@itemForm.mgrs:MGRS`,
        value:
          this.heroReport.mgrs ||
          mgrsFromLatLon(this.heroReport.latitude, this.heroReport.longitude) ||
          $localize`:@@common.unknown:Unknown`,
      },
    ];

    this.standardOptions = [
      {
        label: $localize`:@@heroReport.firstNoticedDate:First Noticed Date`,
        value: !this.heroReport.firstNoticedDate
          ? $localize`:@@common.unknown:Unknown`
          : new Date(this.heroReport.firstNoticedDate).toDateString(),
      },
      {
        label: $localize`:@@heroReport.previousFindings:Previous Findings`,
        value: convertToYesNoOrUnknown(this.heroReport.previousFindings),
      },
      {
        label: $localize`:@@heroReport.wasItemMoved:Was Item Moved`,
        value: convertToYesNoOrUnknown(this.heroReport.wasItemMoved),
      },
      {
        label: $localize`:@@heroReport.pocName:POC Name`,
        value: this.heroReport.pocName || $localize`:@@common.unknown:Unknown`,
      },
      {
        label: $localize`:@@heroReport.pocPhone:POC Phone`,
        value: this.heroReport.pocPhone || $localize`:@@common.unknown:Unknown`,
      },
      {
        label: $localize`:@@heroReport.liquidOrCrystallization:Liquid or Crystallization`,
        value: convertToYesNoOrUnknown(this.heroReport.liquidOrCrystallization),
      },
    ];
    if (this.heroReport.liquidOrCrystallization) {
      this.standardOptions.push(
        {
          label: $localize`:@@heroReport.deadAnimalsOrFoliage:Dead Animals or Foliage`,
          value: convertToYesNoOrUnknown(this.heroReport.deadAnimalsOrFoliage),
        },
        {
          label: $localize`:@@heroReport.casualties:Any casualties or injuries?`,
          value: this.heroReport.casualties || $localize`:@@common.unknown:Unknown`,
        }
      );
    }

    this.evacuationOptions1 = [
      {
        label: $localize`:@@heroReport.evacuationPerimeter:Evacuation Perimeter (m)`,
        value: this.heroReport.evacuationPerimeterM?.toString() || $localize`:@@common.unknown:Unknown`,
      },
      {
        label: $localize`:@@heroReport.infrastructurePresent:Infrastructure Present`,
        value: convertToYesNoOrUnknown(this.heroReport.infrastructurePresent),
      },
    ];

    if (this.heroReport.infrastructurePresent) {
      this.evacuationOptions2 = [
        {
          label: $localize`:@@heroReport.radioTransmitters:Radio Transmitters`,
          value: convertToYesNoOrUnknown(this.heroReport.radioTransmitters),
        },
        {
          label: $localize`:@@heroReport.powerLines:Power Lines`,
          value: convertToYesNoOrUnknown(this.heroReport.powerLines),
        },
        {
          label: $localize`:@@heroReport.fuelTanks:Fuel Tanks`,
          value: convertToYesNoOrUnknown(this.heroReport.fuelTanks),
        },
        {
          label: $localize`:@@heroReport.buildingsInFragZone:Buildings In Frag Zone`,
          value: convertToYesNoOrUnknown(this.heroReport.buildingsInFragZone),
        },
      ];
    } else {
      this.evacuationOptions2 = [];
    }

    this.evacuationOptions3 = [
      {
        label: $localize`:@@heroReport.additionalHazards:Additional Hazards & Accessibility`,
        value: this.heroReport.additionalHazards
          ? this.heroReport.additionalHazards
          : this.heroReport.accessibility || $localize`:@@common.unknown:Unknown`,
      },
    ];

    this.buildImageGroups();
  }

  /** The read-only image groups (one per image type) from the images downloaded so far. */
  private buildImageGroups(): void {
    this.images = [];
    if (this.heroReport.heroReportImages?.length > 0) {
      const groupedMap = this.heroReport.heroReportImages.reduce(
        (acc, image) => {
          if (image.type === 'EOD') return acc; // skip EOD images if desired
          const url = this.imageUrls.get(image.id) ?? image.url;
          if (!url) return acc;
          if (!acc[image.type]) {
            acc[image.type] = [];
          }
          acc[image.type].push(url);
          return acc;
        },
        {} as Record<string, string[]>
      );

      const imageTypeLabels: Record<string, string> = {
        'OVERALL': $localize`:@@imageType.overall:Overall`,
        'NOSE': $localize`:@@imageType.nose:Nose`,
        'TAIL': $localize`:@@imageType.tail:Tail`,
      };

      this.images = Object.keys(groupedMap).map((type) => ({
        label: imageTypeLabels[type] || type,
        value: groupedMap[type],
      }));
    }
  }

  /** Switch to editing a particular tab. */
  editTab(tabIndex: number): void {
    this.currentlyEditingTab = tabIndex;
  }

  /** Cancel editing, revert form data. */
  cancelEdit(tabIndex: number): void {
    this.populateForms();
    this.currentlyEditingTab = null;
  }

  /** Block tab switching if we are editing a different tab. */
  onTabChange(event: any): void {
    const newIndex = event.index;
    if (
      this.currentlyEditingTab !== null &&
      this.currentlyEditingTab !== newIndex
    ) {
      event.originalEvent.preventDefault();
    } else {
      this.activeTabIndex = newIndex;
    }
  }

  /** Used to disable other tabs if editing. */
  isTabDisabled(index: number): boolean {
    return (
      this.currentlyEditingTab !== null && this.currentlyEditingTab !== index
    );
  }

  goBack(): void {
    this.router.navigate(['/events'], { queryParams: { recenter: 'true' } });
  }

  async deleteHeroReport(): Promise<void> {
    if (confirm($localize`:@@deleteHeroConfirm:Are you sure you want to delete this hero report? This action cannot be undone.`)) {
      try {
        await this.heroReportService.delete(this.heroReport.id);
        this.router.navigate(['/events'], { queryParams: { recenter: 'true' } });
      } catch (error) {
        console.error('Failed to delete hero report:', error);
      }
    }
  }

  // Called every time the user selects a new classification in the dropdown
  onClassificationSelect(event: any): void {
    this.itemForm.patchValue({ possibleIdentification: event.value });
  }

  private formArray(name: string): FormArray {
    return this.itemForm.get(name.toLowerCase() + 'Images') as FormArray;
  }

  onItemImageAdd(event: Image) {
    this.formArray(event.tag).push(new FormControl(event));

    this.heroReport.heroReportImages.push({ type: event.tag, url: event.url });
    if (event.tag === 'OVERALL') this.overallImages.push(event);
    else if (event.tag === 'NOSE') this.noseImages.push(event);
    else if (event.tag === 'TAIL') this.tailImages.push(event);
  }

  onItemImageDelete({ tag, url }: Image) {
    // const arr = this.formArray(tag);
    // const idx = arr.value.findIndex((img: Image) => img.url === url);
    // if (idx > -1) arr.removeAt(idx);
    // this.heroReport.heroReportImages = this.heroReport.heroReportImages.filter(
    //   img => !(img.type === tag && img.url === url)
    // );
    // if (tag === 'OVERALL')      this.overallImages = this.overallImages.filter(i => i.url !== url);
    // else if (tag === 'NOSE')    this.noseImages    = this.noseImages.filter(i => i.url !== url);
    // else if (tag === 'TAIL')    this.tailImages    = this.tailImages.filter(i => i.url !== url);
  }

  saveTab(tabIndex: number): void {
    let form: FormGroup;
    if (tabIndex === 0) form = this.itemForm;
    else if (tabIndex === 1) form = this.eventForm;
    else form = this.evacuationForm;

    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    const updated = form.value;

    if (tabIndex === 0) {
      // ITEM
      this.heroReport.possibleIdentification = updated.possibleIdentification;
      // Editing the MGRS moves the position (see linkLocationControls).
      this.heroReport.latitude = updated.latitude;
      this.heroReport.longitude = updated.longitude;
      this.heroReport.mgrs = normalizeMgrs(updated.mgrs);

      // find the classification object
      let maxPerimeter = -1;
      this.heroReport.possibleIdentification.forEach((id: string) => {
        const matched = CLASSIFICATION_OPTIONS.find((opt) => opt.key === id);
        if (matched) {
          maxPerimeter = Math.max(maxPerimeter, matched.evacuationRadius);
        }
      });

      this.heroReport.evacuationPerimeterM =
        maxPerimeter >= 0 ? maxPerimeter : null;
    } else if (tabIndex === 1) {
      this.heroReport.firstNoticedDate = updated.firstNoticedDate;
      this.heroReport.pocName = updated.pocName;
      this.heroReport.pocPhone = updated.pocPhone;
      this.heroReport.previousFindings = updated.previousFindings;
      this.heroReport.wasItemMoved = updated.wasItemMoved;
      this.heroReport.liquidOrCrystallization = updated.liquidOrCrystallization;
      this.heroReport.deadAnimalsOrFoliage = updated.deadAnimalsOrFoliage;
      this.heroReport.casualties = updated.casualties;
    } else {
      // EVACUATION
      this.heroReport.infrastructurePresent = updated.infrastructurePresent;
      this.heroReport.radioTransmitters = updated.radioTransmitters;
      this.heroReport.powerLines = updated.powerLines;
      this.heroReport.fuelTanks = updated.fuelTanks;
      this.heroReport.buildingsInFragZone = updated.buildingsInFragZone;
      this.heroReport.additionalHazards = updated.additionalHazards;
      this.heroReport.accessibility = '';
    }

    this.heroReportService.update(this.heroReport);

    // Refresh read-only data
    this.setReadOnlyData();
    this.currentlyEditingTab = null;
  }
}
